/**
 * The analysis layers in the 2D view, drawn inside TopView's SVG in local metres (north up, y flipped):
 * - under the design: one heatmap (height above ground, irradiance of the year or a month, solar access, TSRF) as a
 *   single image placed on its grid, height contour lines, and the shade of the moment (Sun & shade);
 * - over the design: panels coloured by their yearly production, and the cross-section line.
 *
 * Images are painted once per layer change (a canvas → data URL, memoised); the shade image follows the sun
 * through the store with no React render (its `href` is set directly, at most once per animation frame).
 */
import { memo, useEffect, useMemo, useRef } from "react";
import type { MessageKey } from "../../i18n/keys";
import type { Vec2 } from "../geo.ts";
import { colorAt, contourStep, normalize, paint, rgbCss, type Domain, type ScaleName } from "./colors.ts";
import type { AnalysisData } from "./data.ts";
import { contourSegments, segmentsPath, shadeMask, svgRect, type Raster } from "./grid.ts";
import type { PanelYield } from "./panels.ts";
import { useAnalysisState, type AnalysisState, type AnalysisStore } from "./store.ts";
import { sunAtSolarTime } from "./sun.ts";

export const ACCESS_DOMAIN: Domain = { min: 0.4, max: 1 };
/** The height map fades out towards the ground so the imagery shows around the house. */
const HEIGHT_ALPHA: [number, number] = [70, 255];

export interface ActiveRaster {
  raster: Raster;
  scale: ScaleName;
  domain: Domain;
  /** The legend's title (a catalog key: resolve it with t()). */
  label: MessageKey;
  units: string;
  digits: number;
  percent?: boolean;
}

/** The heatmap the state asks for, with its colour scale and domain (null: none, or no data for it). */
export function activeRaster(data: AnalysisData | null, s: Pick<AnalysisState, "raster" | "month">): ActiveRaster | null {
  if (!data) return null;
  switch (s.raster) {
    case "height": {
      const r = data.heightAboveGround;
      return r ? { raster: r, scale: "height", domain: { min: 0, max: Math.max(3, Math.ceil(r.max)) }, label: "editor.dock.raster.heightAboveGround", units: "m", digits: 1 } : null;
    }
    case "irradiance": {
      if (s.month > 0 && data.monthly) {
        const all = data.monthly;
        const domain = { min: Math.min(...all.map((m) => m.min)), max: Math.max(...all.map((m) => m.max)) };
        return { raster: all[s.month - 1]!, scale: "sun", domain, label: "editor.dock.raster.sunMonth", units: "kWh/kW", digits: 0 };
      }
      const r = data.annual;
      return r ? { raster: r, scale: "sun", domain: { min: Math.max(0, r.min), max: r.max }, label: "editor.dock.raster.sunYear", units: "kWh/kW", digits: 0 } : null;
    }
    case "access":
      return data.access ? { raster: data.access, scale: "sun", domain: ACCESS_DOMAIN, label: "editor.layers.access", units: "%", digits: 0, percent: true } : null;
    case "tsrf":
      return data.tsrf ? { raster: data.tsrf, scale: "sun", domain: ACCESS_DOMAIN, label: "editor.layers.tsrf", units: "%", digits: 0, percent: true } : null;
    default:
      return null;
  }
}

function toDataUrl(pixels: Uint8ClampedArray, cols: number, rows: number): string | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.putImageData(new ImageData(pixels, cols, rows), 0, 0);
  return canvas.toDataURL("image/png");
}

const RasterImage = memo(function RasterImage({ active, opacity }: { active: ActiveRaster; opacity: number }) {
  const { raster, scale, domain } = active;
  const alpha = scale === "height" ? HEIGHT_ALPHA : 255;
  const href = useMemo(() => toDataUrl(paint(raster.values, scale, domain, alpha), raster.grid.cols, raster.grid.rows), [raster, scale, domain, alpha]);
  if (!href) return null;
  return <image className="an-raster" href={href} {...svgRect(raster.grid)} preserveAspectRatio="none" opacity={opacity} />;
});

const Contours = memo(function Contours({ height }: { height: Raster }) {
  const paths = useMemo(() => {
    const step = contourStep(height.max);
    const out: { level: number; d: string; major: boolean }[] = [];
    for (let level = step; level < height.max; level += step) {
      const d = segmentsPath(contourSegments(height, level));
      if (d) out.push({ level, d, major: Math.abs(level / (step * 5) - Math.round(level / (step * 5))) < 1e-6 || level % 5 === 0 });
    }
    return out;
  }, [height]);
  return (
    <g className="an-contours" pointerEvents="none">
      {paths.map((p) => (
        <path key={p.level} d={p.d} className={p.major ? "major" : undefined} vectorEffect="non-scaling-stroke" />
      ))}
    </g>
  );
});

/** The shade of the moment over the site, recomputed from the DSM when the sun moves (no React render). */
function ShadeImage({ surface, store, lat }: { surface: Raster; store: AnalysisStore; lat: number }) {
  const ref = useRef<SVGImageElement>(null);
  useEffect(() => {
    const { cols, rows } = surface.grid;
    const canvas = document.createElement("canvas");
    canvas.width = cols;
    canvas.height = rows;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const pixels = new ImageData(cols, rows);
    const mask = new Uint8Array(cols * rows);
    let raf = 0;
    let last = "";
    const draw = () => {
      raf = 0;
      const s = store.get();
      const key = `${s.day}:${Math.round(s.minutes)}`;
      if (key === last || !ref.current) return;
      last = key;
      const sun = sunAtSolarTime(lat, s.day, s.minutes);
      shadeMask(surface, sun.azimuth, sun.elevation, mask);
      const night = sun.elevation <= 0;
      // shade dark blue, sun a light warm veil: the two read apart over dark and bright imagery alike
      for (let i = 0; i < mask.length; i++) {
        const m = mask[i]!;
        const shaded = m === 1;
        pixels.data[i * 4] = shaded ? 10 : 255;
        pixels.data[i * 4 + 1] = shaded ? 16 : 236;
        pixels.data[i * 4 + 2] = shaded ? 56 : 170;
        pixels.data[i * 4 + 3] = m === 255 ? 0 : shaded ? (night ? 200 : 165) : 52;
      }
      ctx.putImageData(pixels, 0, 0);
      ref.current.setAttribute("href", canvas.toDataURL("image/png"));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(draw);
    };
    schedule();
    const unsubscribe = store.subscribe(schedule);
    return () => {
      unsubscribe();
      cancelAnimationFrame(raf);
    };
  }, [surface, store, lat]);
  return <image ref={ref} className="an-shade" {...svgRect(surface.grid)} preserveAspectRatio="none" pointerEvents="none" />;
}

export function AnalysisUnderlay({ data, store }: { data: AnalysisData | null; store: AnalysisStore }) {
  const raster = useAnalysisState(store, (s) => s.raster);
  const month = useAnalysisState(store, (s) => s.month);
  const contours = useAnalysisState(store, (s) => s.contours);
  const sun = useAnalysisState(store, (s) => s.sun);
  const active = useMemo(() => activeRaster(data, { raster, month }), [data, raster, month]);
  if (!data) return null;
  return (
    <g className="an-underlay" pointerEvents="none">
      {active && <RasterImage active={active} opacity={raster === "height" ? 0.85 : 0.88} />}
      {raster === "height" && contours && data.heightAboveGround && <Contours height={data.heightAboveGround} />}
      {sun && data.surface && <ShadeImage surface={data.surface} store={store} lat={data.sun.lat} />}
    </g>
  );
}

const ring = (pts: Vec2[]) => pts.map(([x, y]) => `${x},${-y}`).join(" ");

export function kwhDomain(yields: PanelYield[]): Domain {
  const v = yields.map((y) => y.kwh).filter(Number.isFinite);
  if (!v.length) return { min: 0, max: 1 };
  const min = Math.min(...v);
  const max = Math.max(...v);
  return max - min < 1 ? { min: min - 10, max: max + 10 } : { min, max };
}

export function AnalysisOverlay({ store, yields, mpp }: { store: AnalysisStore; yields: PanelYield[] | null; mpp: number }) {
  const panelKwh = useAnalysisState(store, (s) => s.panelKwh);
  const section = useAnalysisState(store, (s) => s.section);
  const domain = useMemo(() => (yields ? kwhDomain(yields) : null), [yields]);
  return (
    <g className="an-overlay" pointerEvents="none">
      {panelKwh &&
        yields &&
        domain &&
        yields.map((y) =>
          Number.isFinite(y.kwh) ? (
            <polygon
              key={y.key}
              points={ring(y.plan)}
              className={`an-panel${y.source === "estimate" ? " estimate" : ""}`}
              fill={rgbCss(colorAt("sun", normalize(y.kwh, domain)), 0.92)}
              vectorEffect="non-scaling-stroke"
            />
          ) : null,
        )}
      {section && (
        <g className="an-section">
          <line x1={section.a[0]} y1={-section.a[1]} x2={section.b[0]} y2={-section.b[1]} vectorEffect="non-scaling-stroke" />
          {(["a", "b"] as const).map((k) => (
            <g key={k}>
              <circle cx={section[k][0]} cy={-section[k][1]} r={6 * mpp} />
              <text x={section[k][0]} y={-section[k][1] - 10 * mpp} fontSize={12 * mpp}>
                {k.toUpperCase()}
              </text>
            </g>
          ))}
        </g>
      )}
    </g>
  );
}
